import {
  Button,
  CameraIcon,
  DocumentIcon,
  Flex,
  Stack,
  theme,
} from '@kvdbil/components';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useRef, useState } from 'react';
import { PreviewImage } from './PreviewImage.tsx';
import { getUrlFromFile } from '@utils/image.ts';

const UploadedImage = styled.div`
  max-width: 100%;
  width: 335px;
  cursor: pointer;
  img {
    max-width: 100%;
  }
`;

const StyledButton = styled(Button)`
  width: 100%;
  text-align: center;
  justify-content: center;
`;

const StyledCard = styled(Flex)`
  padding: 1.5rem 1rem;
  background-color: ${theme.colors.background.light};
`;

const UploadButton = styled.div`
  justify-content: center;
  cursor: pointer;
  max-width: 100%;
  width: 335px;
  height: 250px;
  border: 2px dashed ${theme.colors.grayscaleToned.light1};
  border-radius: 16px;
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.75rem;
  line-height: 1.5rem;
  color: ${theme.colors.text.dark};
  font-weight: 600;
`;

const UploadIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: ${theme.colors.info.main};
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${theme.colors.background.light};
`;

interface Props {
  handleUploadButtonClick: (file: File) => void;
}

export const UploadCard = ({ handleUploadButtonClick }: Props) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (!files) {
      setFile(null);
      setFileUrl(null);
      return;
    }
    const images = await Promise.all(
      Array.from(files || []).map(file => getUrlFromFile(file))
    );

    setFileUrl(images[0] as string);
    setFile(files[0]);
  };

  const handleImageButtonClick = () => {
    fileInputRef?.current?.click();
  };

  const clearFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      handleFileChange({
        target: fileInputRef.current,
      } as React.ChangeEvent<HTMLInputElement>);
    }
    setIsOpen(false);
  };

  return (
    <StyledCard grow={1}>
      <Stack>
        {fileUrl && (
          <>
            <UploadedImage onClick={() => setIsOpen(true)}>
              <img
                src={fileUrl}
                alt={'delivery'}
                data-testid={'uploaded-image'}
              />
            </UploadedImage>
            <PreviewImage
              fileUrl={fileUrl}
              isOpen={isOpen}
              setIsOpen={setIsOpen}
              deleteImage={clearFileInput}
            />
          </>
        )}
        {!fileUrl && (
          <UploadButton onClick={handleImageButtonClick}>
            <UploadIcon>
              <CameraIcon width={24} height={24} />
            </UploadIcon>
            {t('Photo of passport/ID')}
          </UploadButton>
        )}
        <input
          type="file"
          style={{ display: 'none' }}
          ref={fileInputRef}
          onChange={handleFileChange}
          accept="image/*"
          data-testid="file-input"
        />

        <StyledButton
          onClick={() => file && handleUploadButtonClick(file)}
          color="info"
          prefixIcon={<DocumentIcon />}
          isDisabled={!file}
        >
          {t('Create delivery receipt')}
        </StyledButton>
      </Stack>
    </StyledCard>
  );
};
